/**
 * Date utilities for operations tasks
 * All dates are displayed in America/Montreal timezone
 */

import { parseISO, startOfDay } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

/**
 * The canonical timezone for the application
 * Per CLAUDE.md: "IMPORTANT: Always use America / Montreal timezone"
 */
export const APP_TIMEZONE = 'America/Montreal';

/**
 * Format a date string in Montreal timezone
 * @param dateStr ISO date string from the backend
 * @param formatStr date-fns format string
 * @returns Formatted date string in Montreal time
 */
export function formatInMontrealTz(dateStr: string | undefined, formatStr: string): string {
  if (!dateStr) return '-';

  try {
    const date = parseISO(dateStr);
    return formatInTimeZone(date, APP_TIMEZONE, formatStr);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
}

/**
 * Get today's date at midnight in Montreal timezone
 * Used for comparing due dates
 */
export function getTodayInMontrealTz(): Date {
  const now = new Date();
  // Convert current time to Montreal timezone
  const zonedNow = toZonedTime(now, APP_TIMEZONE);
  // Get start of day in Montreal timezone
  return startOfDay(zonedNow);
}

/**
 * Parse a date string and return a Date object at midnight in Montreal timezone
 */
export function parseDateInMontrealTz(dateStr: string): Date {
  const date = parseISO(dateStr);
  // Convert to Montreal timezone and get start of day
  const zonedDate = toZonedTime(date, APP_TIMEZONE);
  return startOfDay(zonedDate);
}

/**
 * Check if a date is overdue (before today) in Montreal timezone
 */
export function isOverdue(dateStr: string | undefined, status: string): boolean {
  if (!dateStr) return false;
  if (status === 'done' || status === 'cancelled') return false;

  const dueDate = parseDateInMontrealTz(dateStr);
  const today = getTodayInMontrealTz();

  return dueDate < today;
}

/**
 * Check if a date is today in Montreal timezone
 */
export function isToday(dateStr: string | undefined): boolean {
  if (!dateStr) return false;

  const date = parseDateInMontrealTz(dateStr);
  const today = getTodayInMontrealTz();

  return date.getTime() === today.getTime();
}

/**
 * Format a due date for display with special handling for "Today"
 */
export function formatDueDate(dateStr: string | undefined): string | null {
  if (!dateStr) return null;

  if (isToday(dateStr)) {
    return 'Today';
  }

  return formatInMontrealTz(dateStr, 'MMM d');
}

/**
 * Format a datetime for display (e.g., "Jan 12, 2026 3:45 PM")
 */
export function formatDateTime(dateStr: string | undefined): string {
  return formatInMontrealTz(dateStr, 'MMM d, yyyy h:mm a');
}

/**
 * Format a date for display (e.g., "Jan 12, 2026")
 */
export function formatDate(dateStr: string | undefined): string {
  return formatInMontrealTz(dateStr, 'MMM d, yyyy');
}
